import { parseHydraDocumentation } from '@api-platform/api-doc-parser';

import { NETWORK_ERROR_MESSAGE } from '@/api/apiError';

/**
 * Detection des ressources exposees par API Platform.
 *
 * L'API de ce projet **installe** API Platform (`api-platform/core ^4.3`, routee
 * sur `/api/v1`) mais ne declare **aucune** classe `#[ApiResource]` : elle est
 * ecrite en controleurs `#[Route]` minces sur Doctrine DBAL, sans entites ORM.
 * Son entrypoint Hydra et sa documentation sont donc **coupes a dessein**
 * (`enable_entrypoint: false`, `enable_docs: false` dans
 * `backend/config/packages/api_platform.yaml`) : ils ne decrivaient rien.
 *
 * Ce module ne masque pas ce fait, il le mesure. Il interroge l'entrypoint reel
 * et rend un etat honnete :
 *
 * - `resources` : ce qu'API Platform decrit effectivement. Vide aujourd'hui.
 * - `status` : pourquoi c'est vide — entrypoint coupe, documentation en erreur,
 *   API injoignable, ou documentation presente mais sans ressource.
 *
 * Le jour ou une entite sera annotee `#[ApiResource]` et l'entrypoint rallume,
 * elle apparaitra ici sans qu'une ligne de ce fichier change. C'est ce qui
 * distingue une detection d'une liste ecrite a la main.
 *
 * Le catalogue declaratif (`src/resources/catalog.ts`) reste la source des ecrans
 * servis aujourd'hui : les deux coexistent, et le tableau de bord affiche les
 * deux.
 *
 * ## Pourquoi une sonde avant le parseur
 *
 * `parseHydraDocumentation` **enveloppe toutes ses pannes dans un objet nu**
 * `{api, error, response, status}` — pas une `Error`. Trois situations tres
 * differentes en sortent avec la meme forme :
 *
 * | situation reelle                     | `error.message`                      | `status`    |
 * | ------------------------------------ | ------------------------------------ | ----------- |
 * | API saine, entrypoint coupe (404)    | `The response has no "Link" HTTP...` | `undefined` |
 * | API injoignable / prevol CORS refuse | `fetch failed`                       | `undefined` |
 * | 404 HTML (mauvais port, proxy)       | absent                               | `404`       |
 *
 * Les confondre a produit le defaut corrige ici : le tableau de bord annoncait
 * « L'API est injoignable […] CORS_ALLOWED_ORIGINS (cause inconnue) » alors que
 * l'API repondait parfaitement — elle ne sert simplement pas d'entrypoint Hydra.
 * Un administrateur allait alors chercher un probleme de CORS inexistant.
 *
 * La sonde ci-dessous interroge l'entrypoint **nous-memes**, une fois, avant de
 * confier quoi que ce soit au parseur. Elle distingue ce que le parseur aplatit :
 * un `fetch` qui rejette est une vraie panne de transport ; un 404 est une
 * configuration ; un 2xx merite le parseur. Sur l'etat actuel du backend elle ne
 * coute aucune requete de plus (on s'arrete au 404) ; elle en coute une seule le
 * jour ou l'entrypoint repondra.
 */

export type HydraDiscoveryStatus =
  /** Documentation lue, au moins une ressource decrite. */
  | 'resources-found'
  /** Documentation lue, aucune ressource decrite. */
  | 'documentation-empty'
  /**
   * L'API repond, mais ne sert pas d'entrypoint Hydra. Etat **attendu**
   * aujourd'hui : `enable_entrypoint: false` cote backend. Ce n'est pas une panne.
   */
  | 'entrypoint-disabled'
  /** L'API repond, mais sa documentation est en erreur ou illisible. */
  | 'documentation-error'
  /** Aucune reponse : API arretee, DNS muet, ou prevol CORS refuse. */
  | 'unreachable';

export interface HydraResourceSummary {
  readonly name: string;
  readonly url: string;
  readonly fieldCount: number;
  /** Operations decrites par Hydra (`list`, `create`, …), telles quelles. */
  readonly operations: readonly string[];
}

export interface HydraDiscoveryResult {
  readonly status: HydraDiscoveryStatus;
  readonly resources: readonly HydraResourceSummary[];
  /** Explication en francais, affichable telle quelle. */
  readonly detail: string;
}

/**
 * Le parseur du client API Platform attend un `fetch` compatible et pose lui-meme
 * l'en-tete `Authorization` via l'option `headers`. On lui passe une fonction
 * plutot qu'un objet : l'entrepot des jetons peut avoir change entre le montage
 * de l'application et l'appel.
 */
export interface HydraDiscoveryDependencies {
  readonly apiUrl: string;
  readonly authorizationHeader: () => Record<string, string>;
  /** Injectable pour que les tests n'aient pas a atteindre le reseau. */
  readonly fetchImpl?: typeof fetch;
}

interface ParsedField {
  readonly name?: unknown;
}

interface ParsedOperation {
  readonly type?: unknown;
  readonly name?: unknown;
}

interface ParsedResource {
  readonly name?: unknown;
  readonly url?: unknown;
  readonly fields?: unknown;
  readonly operations?: unknown;
}

const operationNames = (operations: unknown): string[] => {
  if (!Array.isArray(operations)) {
    return [];
  }

  return operations
    .map((operation: ParsedOperation): string | null => {
      if (typeof operation.name === 'string' && operation.name !== '') {
        return operation.name;
      }

      return typeof operation.type === 'string' ? operation.type : null;
    })
    .filter((name): name is string => name !== null);
};

const summarize = (resource: ParsedResource): HydraResourceSummary => ({
  name: typeof resource.name === 'string' ? resource.name : 'ressource sans nom',
  url: typeof resource.url === 'string' ? resource.url : '',
  fieldCount: Array.isArray(resource.fields)
    ? (resource.fields as ParsedField[]).length
    : 0,
  operations: operationNames(resource.operations),
});

/**
 * Rend lisible n'importe quel rejet, y compris l'objet nu du parseur.
 *
 * L'ancienne version testait `cause instanceof Error` et retombait sur
 * « cause inconnue » — ce qui etait **toujours** le cas, le parseur ne rejetant
 * jamais une `Error`. Le seul indice de diagnostic disponible etait donc perdu a
 * chaque fois. On deballe ici les trois formes reellement rencontrees.
 */
export const describeDiscoveryFailure = (cause: unknown): string => {
  if (cause instanceof Error) {
    return cause.message;
  }

  if (typeof cause === 'string' && cause !== '') {
    return cause;
  }

  if (typeof cause === 'object' && cause !== null) {
    const record = cause as Record<string, unknown>;
    const inner: unknown = record.error;

    if (inner instanceof Error) {
      return inner.message;
    }

    if (typeof inner === 'string' && inner !== '') {
      return inner;
    }

    const response: unknown = record.response;
    const status: unknown =
      typeof record.status === 'number'
        ? record.status
        : typeof response === 'object' && response !== null
          ? (response as Record<string, unknown>).status
          : undefined;

    if (typeof status === 'number') {
      return `statut HTTP ${String(status)}`;
    }
  }

  return 'cause inconnue';
};

/**
 * Message de l'etat attendu aujourd'hui.
 *
 * Il nomme le reglage exact et le fichier qui le porte : c'est la difference
 * entre un avertissement qui envoie chercher et une information qui referme la
 * question.
 */
const entrypointDisabledDetail = (apiUrl: string, statusLabel: string): string =>
  `L’API répond (${apiUrl} → ${statusLabel}) mais ne sert pas d’entrypoint Hydra. ` +
  'C’est le réglage voulu : le backend n’expose aucune classe #[ApiResource] et sert ses données ' +
  'par des contrôleurs #[Route], donc `enable_entrypoint: false` et `enable_docs: false` dans ' +
  'backend/config/packages/api_platform.yaml. La documentation réelle de l’API est servie par ' +
  'NelmioApiDocBundle sur /api/v1/docs. Les écrans ci-dessous viennent du catalogue déclaratif ; ' +
  'toute ressource annotée #[ApiResource] à l’avenir apparaîtra ici automatiquement. ' +
  'Ce n’est ni une panne, ni un problème de CORS.';

export const discoverHydraResources = async ({
  apiUrl,
  authorizationHeader,
  fetchImpl = globalThis.fetch.bind(globalThis),
}: HydraDiscoveryDependencies): Promise<HydraDiscoveryResult> => {
  let probe: Response;

  try {
    // La documentation Hydra est publique sur ce backend, mais poser le jeton
    // quand la session existe evite de dependre de ce detail : si les regles
    // d'acces changent, la detection continuera de fonctionner.
    probe = await fetchImpl(apiUrl, {
      headers: { Accept: 'application/ld+json', ...authorizationHeader() },
    });
  } catch (cause) {
    // `fetch` ne rejette que sur un echec de transport : API arretee, DNS muet,
    // ou prevol CORS refuse. C'est le **seul** cas ou le message CORS est merite.
    return {
      status: 'unreachable',
      resources: [],
      detail: `${NETWORK_ERROR_MESSAGE} (${describeDiscoveryFailure(cause)})`,
    };
  }

  // 404 / 410 : l'entrypoint n'est pas servi. L'API, elle, a repondu — et c'est
  // precisement ce qui interdit de parler de panne ou de CORS ici.
  if (probe.status === 404 || probe.status === 410) {
    return {
      status: 'entrypoint-disabled',
      resources: [],
      detail: entrypointDisabledDetail(apiUrl, String(probe.status)),
    };
  }

  if (!probe.ok) {
    return {
      status: 'documentation-error',
      resources: [],
      detail:
        `L’entrypoint ${apiUrl} a répondu ${String(probe.status)}. L’API est joignable : ` +
        'ce n’est ni une panne réseau ni un refus CORS, mais une erreur côté serveur ou une ' +
        'autorisation refusée sur cette URL.',
    };
  }

  try {
    const { api, status } = await parseHydraDocumentation(apiUrl, {
      headers: () => authorizationHeader(),
    });

    if (typeof status === 'number' && status >= 400) {
      return {
        status: 'documentation-error',
        resources: [],
        detail: `La documentation Hydra a répondu ${String(status)} sur ${apiUrl}/docs.jsonld.`,
      };
    }

    const resources = Array.isArray(api.resources)
      ? (api.resources as ParsedResource[]).map(summarize)
      : [];

    if (resources.length === 0) {
      return {
        status: 'documentation-empty',
        resources: [],
        detail:
          "API Platform répond, mais ne décrit aucune ressource : le backend n'expose aucune classe #[ApiResource] " +
          "et sert ses données par des contrôleurs #[Route]. Les écrans ci-dessous viennent donc du catalogue déclaratif. " +
          'Toute ressource annotée #[ApiResource] à l’avenir apparaîtra ici automatiquement.',
      };
    }

    return {
      status: 'resources-found',
      resources,
      detail: `API Platform décrit ${String(resources.length)} ressource(s) exploitables par introspection.`,
    };
  } catch (cause) {
    // L'entrypoint a repondu 2xx : quoi qu'il arrive ensuite, l'API est
    // joignable. Le seul cas frequent est l'en-tete `Link` absent — soit
    // l'entrypoint n'est pas un document Hydra, soit le navigateur ne peut pas
    // le lire faute d'`Access-Control-Expose-Headers: Link` cote serveur (`Link`
    // n'appartient pas aux en-tetes exposes d'office en CORS).
    return {
      status: 'documentation-error',
      resources: [],
      detail:
        `L’entrypoint ${apiUrl} répond, mais sa documentation Hydra n’a pas pu être lue ` +
        `(${describeDiscoveryFailure(cause)}). L’API est joignable : ce n’est pas un problème de réseau. ` +
        'Si l’en-tête « Link » est en cause, vérifiez qu’API Platform sert bien un entrypoint Hydra et ' +
        'que le serveur l’expose au navigateur (Access-Control-Expose-Headers: Link).',
    };
  }
};
